/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { EuiBasicTableColumn, EuiTableActionsColumnType } from '@elastic/eui'
import { EuiFlexGroup, EuiFlexItem, EuiText, EuiTextColor } from '@elastic/eui'

import type { NormalizedTrafficFilterInfo } from '@modules/network-security-lib/types'
import { FormattedMessage, Unmask } from '@modules/i18n'
import CloudProvider from '@modules/cloud-components/CloudProvider'
import { getCspRegionIdFromRegionId } from '@modules/cloud-lib/providers'

import TrafficFilterType from '../TrafficFilterType'

import HostedDeleteButton from './DeleteButton/HostedDeleteButton'
import ServerlessDeleteButton from './DeleteButton/ServerlessDeleteButton'

import type { IntlShape } from 'react-intl'

export const nameColumn: EuiBasicTableColumn<NormalizedTrafficFilterInfo> = {
  name: <FormattedMessage id='networkSecurity.list.name.header' defaultMessage='Name' />,
  field: 'ruleset.name',
  sortable: true,
  render: (_value, row) => (
    <EuiFlexGroup direction='column' gutterSize='none'>
      <EuiFlexItem>{row.ruleset.name}</EuiFlexItem>
      {row.ruleset.description && (
        <EuiFlexItem>
          <EuiText size='xs' color='subdued'>
            {row.ruleset.description}
          </EuiText>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  ),
}

export const typeColumn: EuiBasicTableColumn<NormalizedTrafficFilterInfo> = {
  name: (
    <FormattedMessage id='networkSecurity.list.ruleset.type.header' defaultMessage='Filter type' />
  ),
  render: (row: NormalizedTrafficFilterInfo) => (
    <div className='eui-textNoWrap'>
      <TrafficFilterType type={row.ruleset.type} />
    </div>
  ),
  width: '11rem',
}

export const defaultColumn: EuiBasicTableColumn<NormalizedTrafficFilterInfo> = {
  name: <FormattedMessage id='networkSecurity.list.default.header' defaultMessage='Default' />,
  render: (row: NormalizedTrafficFilterInfo) =>
    row.ruleset.include_by_default ? (
      <FormattedMessage id='networkSecurity.list.default.yes' defaultMessage='Yes' />
    ) : (
      <FormattedMessage id='networkSecurity.list.default.no' defaultMessage='No' />
    ),
  width: '5rem',
}

export const resourcesColumn: EuiBasicTableColumn<NormalizedTrafficFilterInfo> = {
  name: <FormattedMessage id='networkSecurity.list.usage' defaultMessage='Protected resources' />,
  render: (row: NormalizedTrafficFilterInfo) => (
    <div className='eui-textNoWrap'>
      <EuiTextColor color={row.totalAssociations === 0 ? 'subdued' : 'default'}>
        {row.type === 'serverless' ? (
          <FormattedMessage
            id='networkSecurity.list.projectCount'
            defaultMessage='{count} {count, plural, one {project} other {projects}}'
            values={{ count: row.totalAssociations || 0 }}
          />
        ) : (
          <FormattedMessage
            id='networkSecurity.list.deploymentCount'
            defaultMessage='{count} {count, plural, one {deployment} other {deployments}}'
            values={{ count: row.totalAssociations || 0 }}
          />
        )}
      </EuiTextColor>
    </div>
  ),
}

export const providerColumn: EuiBasicTableColumn<NormalizedTrafficFilterInfo> = {
  name: (
    <FormattedMessage id='networkSecurity.list.ruleset.provider.header' defaultMessage='Provider' />
  ),
  render: (row: NormalizedTrafficFilterInfo) => (
    <div className='eui-textNoWrap'>
      <CloudProvider region={row.ruleset.region} text='short' showIcon={true} />
    </div>
  ),
  width: '7rem',
}

export const regionColumn: EuiBasicTableColumn<NormalizedTrafficFilterInfo> = {
  name: (
    <FormattedMessage id='networkSecurity.list.ruleset.region.header' defaultMessage='Region' />
  ),
  render: (row: NormalizedTrafficFilterInfo) => (
    <div className='eui-textNoWrap'>
      <Unmask>{getCspRegionIdFromRegionId(row.ruleset.region)}</Unmask>
    </div>
  ),
  width: '12rem',
}

export const makeActionsColumn = ({
  formatMessage,
  onEdit,
  canUpdateHosted,
  canUpdateServerless,
  canDeleteHosted,
  canDeleteServerless,
}: {
  formatMessage: IntlShape['formatMessage']
  onEdit: (row: NormalizedTrafficFilterInfo) => void
  canUpdateHosted: boolean
  canUpdateServerless: boolean
  canDeleteHosted: boolean
  canDeleteServerless: boolean
}) => {
  const actions: EuiTableActionsColumnType<NormalizedTrafficFilterInfo>['actions'] = [
    {
      type: 'icon',
      name: (row: NormalizedTrafficFilterInfo) => (
        <FormattedMessage
          id='networkSecurity.list.ruleset.edit.name'
          defaultMessage='Edit {name}'
          values={{ name: row.ruleset.name }}
        />
      ),
      icon: 'pencil',
      onClick: (row: NormalizedTrafficFilterInfo) => onEdit(row),
      description: formatMessage({
        id: 'networkSecurity.list.ruleset.edit.description',
        defaultMessage: 'Edit',
      }),
      enabled: (row: NormalizedTrafficFilterInfo) =>
        row.type === 'serverless' ? canUpdateServerless : canUpdateHosted,
    },
    {
      render: (row: NormalizedTrafficFilterInfo) =>
        row.type === 'serverless' ? (
          <ServerlessDeleteButton
            ruleset={row.ruleset}
            associations={row.totalAssociations || 0}
            canDelete={canDeleteServerless}
          />
        ) : (
          <HostedDeleteButton
            ruleset={row.ruleset}
            associations={row.totalAssociations || 0}
            canDelete={canDeleteHosted}
          />
        ),
    },
  ]

  return {
    name: (
      <FormattedMessage id='networkSecurity.list.ruleset.actions.header' defaultMessage='Actions' />
    ),
    actions,
    width: '7rem',
  }
}
